import { NormalizedDifficulty, Platform } from '../types/enums';

/**
 * Suggests a normalized difficulty from a raw platform value.
 *
 * This is only ever a *suggestion* shown in the UI — `normalizedDifficulty` is
 * always the user's own choice, and EXPERT is reachable only by overriding.
 */
export function suggestDifficulty(
  platform: Platform,
  platformDifficulty: string,
): NormalizedDifficulty | null {
  const raw = platformDifficulty.trim();
  if (raw.length === 0) return null;

  switch (platform) {
    case Platform.CODEFORCES: {
      const rating = Number.parseInt(raw, 10);
      if (Number.isNaN(rating)) return null;
      if (rating < 1200) return NormalizedDifficulty.EASY;
      if (rating < 1700) return NormalizedDifficulty.MEDIUM;
      if (rating < 2200) return NormalizedDifficulty.HARD;
      return NormalizedDifficulty.EXPERT;
    }
    case Platform.LEETCODE: {
      const label = raw.toLowerCase();
      if (label.startsWith('easy')) return NormalizedDifficulty.EASY;
      if (label.startsWith('med')) return NormalizedDifficulty.MEDIUM;
      if (label.startsWith('hard')) return NormalizedDifficulty.HARD;
      return null;
    }
    case Platform.ATCODER: {
      // AtCoder problems are labelled by contest slot: ABC-A … ABC-G, ARC-A …
      const slot = /([A-Ha-h])\s*$/.exec(raw)?.[1]?.toUpperCase();
      if (!slot) return null;
      if (slot <= 'B') return NormalizedDifficulty.EASY;
      if (slot <= 'D') return NormalizedDifficulty.MEDIUM;
      if (slot <= 'F') return NormalizedDifficulty.HARD;
      return NormalizedDifficulty.EXPERT;
    }
    default:
      return null;
  }
}

export const DIFFICULTY_HINTS: Record<Platform, string> = {
  CODEFORCES: '<1200 Хялбар · 1200–1699 Дунд · 1700–2199 Хүнд · ≥2200 Онц хүнд',
  LEETCODE: 'Easy / Medium / Hard шууд тохирно · Онц хүнд зөвхөн гараар',
  ATCODER: 'A–B Хялбар · C–D Дунд · E–F Хүнд · G+ Онц хүнд',
  OTHER: 'Автомат тохируулга байхгүй — өөрт тохирсон түвшнийг сонго',
};

/** EASY = 1 … EXPERT = 4, used to average difficulty in the monthly report. */
export const DIFFICULTY_SCORE: Record<NormalizedDifficulty, number> = {
  EASY: 1,
  MEDIUM: 2,
  HARD: 3,
  EXPERT: 4,
};

export function difficultyFromScore(score: number): NormalizedDifficulty | null {
  if (!Number.isFinite(score) || score <= 0) return null;
  const rounded = Math.round(score);
  const entry = (Object.entries(DIFFICULTY_SCORE) as [NormalizedDifficulty, number][]).find(
    ([, value]) => value === Math.min(4, Math.max(1, rounded)),
  );
  return entry ? entry[0] : null;
}
